/**
 * VH-236: the live preview behind "Calculate nutrition".
 *
 * The panel used to show nothing until the user saved, so a plainly wrong result — 81 kcal a slice
 * for a loaf of bread, because six of seven ingredients had no values — only became visible once
 * it was stored. These are the same checks the server runs in `app/services/recipe_nutrition.py`,
 * applied to the choices the user is still editing, so the warning appears *before* Save.
 *
 * The server stays the authority: it recomputes everything on save and its warnings are what the
 * result carries afterwards.
 */

export const NUTRIENTS = ['kcal', 'protein_g', 'carbs_g', 'fat_g', 'fiber_g', 'sugars_g']

// Flour, oats, sugar, oil: a mix containing one of these cannot plausibly be watery.
// Kept in step with DRY_STAPLE_TOKENS in app/services/recipe_nutrition.py.
const DRY_STAPLE_TOKENS = new Set([
  'flour', 'mehl', 'farinha', 'harina', 'bran', 'kleie', 'semolina', 'griess', 'grieß',
  'oats', 'hafer', 'haferflocken', 'rice', 'reis', 'pasta', 'nudeln', 'sugar', 'zucker',
  'oil', 'oel', 'öl', 'butter', 'nuts', 'nuesse', 'nüsse', 'seeds', 'samen', 'kerne',
  'sourdough', 'sauerteig', 'starch', 'staerke', 'stärke', 'couscous', 'bulgur', 'quinoa',
])

export const MIN_SOLID_KCAL_PER_100G = 50
export const MIN_SOLID_KCAL_PER_100G_WITH_STAPLE = 100
export const MACRO_ENERGY_TOLERANCE = 0.25
// A row lighter than this cannot move the totals (1.4 g of yeast in 2 kg of dough).
export const MEANINGFUL_GRAMS = 5
export const MEANINGFUL_WEIGHT_SHARE = 0.01

const tokensOf = (name) =>
  (name ?? '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').split(' ').filter(Boolean)

export const hasDryStaple = (names) =>
  names.some((name) => tokensOf(name).some((word) => DRY_STAPLE_TOKENS.has(word)))

/** The values a row currently contributes per 100 g, or null when the user has chosen nothing. */
export function rowValues(row, choice) {
  if (choice && choice.mode !== 'none') return choice.per_100g ?? null
  return row.per_100g ?? null
}

/**
 * Totals, per-100 g of the finished item, the rows that still block a sensible result, and the
 * plausibility checks. Everything a user needs to see before pressing Save.
 */
export function computePreview({ rows, choices, finishedWeightG, servings }) {
  const serve = Math.max(Number(servings) || 1, 1)
  const totals = Object.fromEntries(NUTRIENTS.map((key) => [key, 0]))
  const rawWeight = rows.reduce((sum, row) => sum + (Number(row.grams) || 0), 0)
  let solidWeight = 0
  let solidKcal = 0
  const missing = []
  const solidNames = []

  rows.forEach((row) => {
    const grams = Number(row.grams) || 0
    const values = rowValues(row, choices?.[row.ingredient_id])
    if (!row.zero_by_nature) {
      solidWeight += grams
      solidNames.push(row.name)
    }
    if (values) {
      NUTRIENTS.forEach((key) => {
        totals[key] += ((Number(values[key]) || 0) * grams) / 100
      })
      if (!row.zero_by_nature) solidKcal += ((Number(values.kcal) || 0) * grams) / 100
      return
    }
    // Water and salt are zero on purpose — never "missing".
    if (row.zero_by_nature || grams <= 0) return
    const share = rawWeight > 0 ? grams / rawWeight : 0
    if (grams >= MEANINGFUL_GRAMS || share >= MEANINGFUL_WEIGHT_SHARE) missing.push(row)
  })

  const finished = Number(finishedWeightG) || 0
  const weight = finished || rawWeight
  const perServing = Object.fromEntries(NUTRIENTS.map((key) => [key, totals[key] / serve]))
  const per100g = weight
    ? Object.fromEntries(NUTRIENTS.map((key) => [key, (totals[key] / weight) * 100]))
    : null

  const warnings = []
  if (missing.length > 0) {
    warnings.push({ code: 'ingredients_without_nutrition', names: missing.map((row) => row.name) })
  }
  if (finished && rawWeight && Math.abs(finished - rawWeight) < 0.5) {
    warnings.push({ code: 'finished_weight_equals_raw', raw_weight_g: rawWeight })
  }
  if (solidWeight > 0 && totals.kcal > 0) {
    const density = (solidKcal / solidWeight) * 100
    const floor = hasDryStaple(solidNames) ? MIN_SOLID_KCAL_PER_100G_WITH_STAPLE : MIN_SOLID_KCAL_PER_100G
    if (density < floor) {
      warnings.push({
        code: 'energy_density_implausible',
        kcal_per_100g_solids: Math.round(density * 10) / 10,
        expected_at_least: floor,
      })
    }
  }
  if (totals.kcal > 0) {
    const fromMacros = 4 * totals.protein_g + 4 * totals.carbs_g + 9 * totals.fat_g
    if (fromMacros > 0 && Math.abs(fromMacros - totals.kcal) / totals.kcal > MACRO_ENERGY_TOLERANCE) {
      warnings.push({
        code: 'macro_energy_mismatch',
        kcal: Math.round(totals.kcal),
        kcal_from_macros: Math.round(fromMacros),
      })
    }
  }

  return {
    rawWeightG: rawWeight || null,
    weightUsedG: weight || null,
    yieldFactor: finished && rawWeight ? Math.round((finished / rawWeight) * 1000) / 1000 : null,
    totals,
    perServing,
    per100g,
    servingWeightG: weight ? weight / serve : null,
    missing,
    warnings,
    hasValues: Object.values(totals).some((value) => value > 0),
  }
}

/** Rows whose chosen food came from a match the matcher itself is unsure about. */
export function weakMatches(rows, choices) {
  return rows.filter((row) => {
    const choice = choices?.[row.ingredient_id]
    const weak = row.suggestion && ['low', 'unsicher'].includes(row.suggestion.confidence)
    return weak && choice?.mode === 'matched' && choice.food_id === row.suggestion.food_id
  })
}

export default computePreview
